import {
  WHATSAPP_ACCOUNT_MODE_LABELS,
  WHATSAPP_ACCOUNT_MODES,
  type WhatsappAccountMode,
  type WhatsappAccountTestResult,
} from '@performance-crm/shared';
import { PlugZap, Save, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { fmt } from '@/lib/format';
import {
  useRemoveWhatsappAccount,
  useSaveWhatsappAccount,
  useTestWhatsappAccount,
  useWhatsappAccount,
} from '@/lib/queries';
import { CopyButton } from '@/pages/ingest/IngestPage';

const EMPTY = {
  displayName: '',
  phoneNumber: '',
  phoneNumberId: '',
  wabaId: '',
  accessToken: '',
  mode: 'SIMULADO' as WhatsappAccountMode,
};

/** Número oficial de WhatsApp de la empresa (Fase 8.4): datos de Meta, modo simulado/real y prueba de conexión. */
export function WhatsappAccountTab({ companyId }: { companyId: string }) {
  const { data: account, isLoading } = useWhatsappAccount(companyId);
  const save = useSaveWhatsappAccount();
  const test = useTestWhatsappAccount();
  const remove = useRemoveWhatsappAccount();
  const toast = useToast();
  const [f, setF] = useState(EMPTY);
  const [result, setResult] = useState<WhatsappAccountTestResult | null>(null);
  useEffect(() => {
    if (account)
      setF({
        displayName: account.displayName,
        phoneNumber: account.phoneNumber,
        phoneNumberId: account.phoneNumberId ?? '',
        wabaId: account.wabaId ?? '',
        accessToken: '',
        mode: account.mode,
      });
    else setF(EMPTY);
  }, [account]);
  const set = (patch: Partial<typeof f>) => setF((v) => ({ ...v, ...patch }));
  const webhookUrl = `${window.location.origin}/api/v1/inbound/whatsapp/webhook`;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await save.mutateAsync({
        companyId,
        displayName: f.displayName,
        phoneNumber: f.phoneNumber,
        phoneNumberId: f.phoneNumberId || null,
        wabaId: f.wabaId || null,
        accessToken: f.accessToken || null,
        mode: f.mode,
      });
      set({ accessToken: '' });
      setResult(null);
      toast.success('Número guardado', 'Prueba la conexión para confirmar que todo está bien.');
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  const doTest = async () => {
    try {
      const r = await test.mutateAsync(companyId);
      setResult(r);
      if (r.ok) toast.success('Conexión correcta', r.message);
      else toast.error('La conexión falló', r.message);
    } catch (err) {
      toast.error('No se pudo probar', (err as Error).message);
    }
  };
  const doRemove = async () => {
    if (!confirm('¿Quitar el número? Las conversaciones ya guardadas se conservan.')) return;
    try {
      await remove.mutateAsync(companyId);
      setResult(null);
      toast.success('Número quitado');
    } catch (err) {
      toast.error('No se pudo quitar', (err as Error).message);
    }
  };

  if (isLoading) return <Skeleton className="h-64" />;
  return (
    <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            Número de WhatsApp de la empresa
            {account ? (
              <StatusBadge status={account.status} lastCheckAt={account.lastCheckAt} />
            ) : (
              <Badge tone="muted">Sin número</Badge>
            )}
            {account?.mode === 'SIMULADO' && <Badge tone="warning">Modo simulado</Badge>}
          </CardTitle>
          <CardDescription>
            Cada inmobiliaria escribe a sus clientes desde su propio número oficial (WhatsApp Cloud
            API de Meta). Mientras no tengas el número listo, usa el modo simulado: los mensajes se
            guardan en el CRM sin salir a WhatsApp.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="wa-display-name">Nombre que verá el cliente</Label>
                <Input
                  id="wa-display-name"
                  value={f.displayName}
                  onChange={(e) => set({ displayName: e.target.value })}
                  required
                  minLength={2}
                  placeholder="V&V Inmobiliaria"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="wa-phone">Número (formato internacional)</Label>
                <Input
                  id="wa-phone"
                  value={f.phoneNumber}
                  onChange={(e) => set({ phoneNumber: e.target.value })}
                  required
                  minLength={6}
                  placeholder="+51 999 888 777"
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="wa-mode">Modo</Label>
                <Select
                  id="wa-mode"
                  value={f.mode}
                  onChange={(e) => set({ mode: e.target.value as WhatsappAccountMode })}
                >
                  {WHATSAPP_ACCOUNT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {WHATSAPP_ACCOUNT_MODE_LABELS[m]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="wa-phone-id">Identificador del número (phone_number_id)</Label>
                <Input
                  id="wa-phone-id"
                  value={f.phoneNumberId}
                  onChange={(e) => set({ phoneNumberId: e.target.value })}
                  placeholder="1234567890"
                  required={f.mode === 'REAL'}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="wa-waba">Cuenta de WhatsApp Business (WABA)</Label>
                <Input
                  id="wa-waba"
                  value={f.wabaId}
                  onChange={(e) => set({ wabaId: e.target.value })}
                  placeholder="opcional"
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="wa-token">Token permanente de Meta</Label>
                <Input
                  id="wa-token"
                  type="password"
                  autoComplete="off"
                  value={f.accessToken}
                  onChange={(e) => set({ accessToken: e.target.value })}
                  placeholder={
                    account?.hasToken
                      ? `Guardado (termina en ${account.tokenLast4 ?? '····'}); déjalo vacío para conservarlo`
                      : 'Pégalo aquí (se guarda cifrado y no se vuelve a mostrar)'
                  }
                  required={f.mode === 'REAL' && !account?.hasToken}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" disabled={save.isPending} data-testid="wa-account-save">
                <Save className="h-4 w-4" /> Guardar
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!account || test.isPending}
                onClick={() => void doTest()}
                data-testid="wa-account-test"
              >
                <PlugZap className="h-4 w-4" /> Probar conexión
              </Button>
              {account && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive ml-auto"
                  disabled={remove.isPending}
                  onClick={() => void doRemove()}
                >
                  <Trash2 className="h-4 w-4" /> Quitar número
                </Button>
              )}
            </div>
          </form>
          {result && (
            <p
              className={`mt-3 rounded-md border p-3 text-sm ${result.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30' : 'border-red-200 bg-red-50 text-red-800 dark:bg-red-950/30'}`}
              data-testid="wa-account-result"
            >
              {result.message}
            </p>
          )}
          {account?.status === 'ERROR' && account.lastError && !result && (
            <p className="text-destructive mt-3 text-sm">Última prueba: {account.lastError}</p>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>¿De dónde salen estos datos?</CardTitle>
          <CardDescription>Pasos en Meta para conectar el número real.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ol className="list-decimal space-y-2 pl-4">
            <li>
              Entra a <span className="font-medium">developers.facebook.com</span>, abre tu app y ve
              a <span className="font-medium">WhatsApp → Configuración de la API</span>.
            </li>
            <li>
              Copia el <span className="font-medium">identificador del número de teléfono</span>{' '}
              (phone_number_id) y el de la{' '}
              <span className="font-medium">cuenta de WhatsApp Business</span> (WABA).
            </li>
            <li>
              En <span className="font-medium">Meta Business → Usuarios del sistema</span> crea un
              usuario, dale acceso a la app y genera un token permanente con los permisos{' '}
              <code>whatsapp_business_messaging</code> y <code>whatsapp_business_management</code>.
            </li>
            <li>
              En <span className="font-medium">WhatsApp → Configuración → Webhooks</span> registra
              esta URL, suscribe el campo <code>messages</code> y usa el token de verificación que
              te dé el administrador del CRM.
            </li>
          </ol>
          <p className="bg-muted flex items-center gap-2 break-all rounded-md p-2 font-mono text-xs">
            {webhookUrl} <CopyButton value={webhookUrl} />
          </p>
          <p className="text-muted-foreground text-xs">
            Guía completa: <code>docs/integrations/whatsapp-cloud.md</code> en el repositorio.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({
  status,
  lastCheckAt,
}: {
  status: 'SIN_PROBAR' | 'OK' | 'ERROR';
  lastCheckAt: string | null;
}) {
  if (status === 'OK')
    return (
      <Badge tone="success" data-testid="wa-account-status">
        Conexión OK · {fmt.dateTime(lastCheckAt)}
      </Badge>
    );
  if (status === 'ERROR')
    return (
      <Badge tone="danger" data-testid="wa-account-status">
        Con error · {fmt.dateTime(lastCheckAt)}
      </Badge>
    );
  return (
    <Badge tone="muted" data-testid="wa-account-status">
      Sin probar
    </Badge>
  );
}
